import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';

import { APP_CONFIG } from '../../core/config/app-config.model';
import { LayoutService } from '../../core/layout/layout.service';
import { SessionService } from '../../core/session/session.service';
import { FaIconComponent, faBars, faMagnifyingGlass } from '../../ui/icons/app-icons.registry';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { ProfileMenuComponent } from './profile-menu.component';

/**
 * Top bar: the sm-band menu control (aria-expanded bound to drawerOpen), the
 * visibly disabled search placeholder (AITH-44 FR-017), the freshness indicator
 * (present only while a page has published a data instant — @if, never CSS),
 * and the sign-out affordance (AITH-43, FR-014 — present only for a real
 * authenticated session, never in mockAuth dev mode).
 */
@Component({
  selector: 'app-top-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // RouterLink is a template dependency of the product lockup. Standalone
  // components inherit nothing from BreadcrumbComponent, and `routerLink="…"`
  // is a static attribute rather than a binding, so omitting it is not a
  // compile error — the anchor simply renders with no href and navigates
  // nowhere. `top-bar.component.spec.ts` asserts the resolved href.
  imports: [RouterLink, FaIconComponent, BreadcrumbComponent, ProfileMenuComponent],
  templateUrl: './top-bar.component.html',
  styleUrl: './top-bar.component.scss',
  // Sm-band layout is class-driven (LayoutService band) — the house convention
  // (sidebar .icon-only): a raw CSS @media query tracks the real viewport and
  // cannot be stubbed via stubMatchMedia in specs.
  host: { '[class.top-bar--sm]': "layout.band() === 'sm'" },
})
export class TopBarComponent {
  protected readonly menuIcon = faBars;
  protected readonly searchIcon = faMagnifyingGlass;
  protected readonly layout = inject(LayoutService);

  private readonly config = inject(APP_CONFIG);
  private readonly session = inject(SessionService);

  /** Mock authentication has no sign-out transition, so it exposes no account action. */
  protected readonly showProfile = computed(
    () => !this.config.mockAuth && this.session.isAuthenticated(),
  );
}
